import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sd } from "@sd/lib/sdPaths";
import { Header } from "@/components/layout/Header";
import { Sidebar } from "@/components/layout/Sidebar";
import { AssetList, Asset } from "@/components/assets/AssetList";
import { AssetDetail } from "@/components/assets/AssetDetail";
import { AddAssetDialog } from "@/components/assets/AddAssetDialog";
import { CommandPalette } from "@/components/CommandPalette";
import {
  ServiceDeskAPI,
  serviceDeskAssetsListQueryKey,
} from "@sd/services/APIs";
import { extractPayloadArray } from "@sd/lib/userDisplayMap";
import { toast } from "sonner";

const ASSET_TYPES = [
  "desktop",
  "laptop",
  "server",
  "printer",
  "mobile",
] as const;

const ASSET_STATUSES = ["active", "inactive", "maintenance"] as const;

function mapRowToAsset(r: unknown): Asset | null {
  if (!r || typeof r !== "object") {
    return null;
  }
  const o = r as Record<string, unknown>;
  const id = String(o.id ?? o.pk ?? o.asset_id ?? "").trim();
  if (!id) {
    return null;
  }
  const name = String(o.name ?? o.asset_name ?? o.title ?? id);
  const typeRaw = String(o.type ?? o.asset_type ?? "desktop").toLowerCase();
  const type: Asset["type"] = ASSET_TYPES.includes(typeRaw as (typeof ASSET_TYPES)[number])
    ? (typeRaw as Asset["type"])
    : "desktop";
  const statusRaw = String(o.status ?? "active").toLowerCase();
  const status: Asset["status"] = ASSET_STATUSES.includes(
    statusRaw as (typeof ASSET_STATUSES)[number]
  )
    ? (statusRaw as Asset["status"])
    : "active";
  const assignedRaw = o.assigned_to ?? o.assignedTo ?? o.assignee ?? null;
  const assignedTo =
    assignedRaw === null || assignedRaw === undefined
      ? null
      : String(assignedRaw).trim() || null;
  const department = String(o.department ?? o.dept ?? "—");
  const lastSeen = String(
    o.last_seen ?? o.lastSeen ?? o.updated_at ?? o.updatedAt ?? "—"
  );
  const os = String(o.os ?? o.operating_system ?? "—");
  const ip = String(o.ip ?? o.ip_address ?? o.ipAddress ?? "—");

  return {
    id,
    name,
    type,
    status,
    assignedTo,
    department,
    lastSeen,
    os,
    ip,
  };
}

export default function AssetManagement() {
  const { queue = "all" } = useParams<{ queue?: string }>();
  const navigate = useNavigate();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [assetSearchQuery, setAssetSearchQuery] = useState("");
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [addAssetOpen, setAddAssetOpen] = useState(false);

  const { data: assetsPayload, isPending, isError } = useQuery({
    queryKey: serviceDeskAssetsListQueryKey,
    queryFn: async () => {
      const res = await ServiceDeskAPI.assets.list();
      return res.data;
    },
    retry: 1,
  });

  const inventoryAssets = useMemo(() => {
    const raw = extractPayloadArray(assetsPayload);
    const out: Asset[] = [];
    for (const row of raw) {
      const a = mapRowToAsset(row);
      if (a) {
        out.push(a);
      }
    }
    return out;
  }, [assetsPayload]);

  const selectedAsset = inventoryAssets.find(a => a.id === selectedAssetId);

  const filteredAssets = useMemo(() => {
    const q = assetSearchQuery.toLowerCase();
    return inventoryAssets.filter(asset => {
      const matchesSearch =
        asset.name.toLowerCase().includes(q) ||
        asset.department.toLowerCase().includes(q);

      if (!matchesSearch) {
        return false;
      }

      switch (queue) {
        case "all":
          return true;
        case "active":
          return asset.status === "active";
        case "inactive":
          return asset.status === "inactive";
        case "maintenance":
          return asset.status === "maintenance";
        case "unassigned":
          return !asset.assignedTo;
        default:
          return true;
      }
    });
  }, [inventoryAssets, assetSearchQuery, queue]);

  useEffect(() => {
    const firstAsset = filteredAssets[0];
    if (firstAsset) {
      setSelectedAssetId(firstAsset.id);
    } else {
      setSelectedAssetId(null);
    }
  }, [queue, filteredAssets]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleAddAsset = () => {
    setAddAssetOpen(true);
  };

  const handleQueueChange = (newQueue: string) => {
    navigate(sd(`assets/${newQueue}`));
  };

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      <Header onSearchClick={() => setCommandPaletteOpen(true)} />

      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        onCreateTicket={() => navigate(sd("tickets/new"))}
        onSelectTicket={() => {}}
      />

      <AddAssetDialog open={addAssetOpen} onOpenChange={setAddAssetOpen} />

      <div className="flex-1 flex overflow-hidden">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          activeQueue={queue}
          onQueueChange={handleQueueChange}
          activeTab="assets"
          onTabChange={tab => {
            if (tab === "tickets") navigate(sd("tickets"));
          }}
        />

        <div className="flex-1 flex overflow-hidden">
          {isPending ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              Loading assets…
            </div>
          ) : isError ? (
            <div className="flex-1 flex items-center justify-center text-destructive text-sm px-4 text-center">
              Could not load assets. Check your connection and try again.
            </div>
          ) : (
            <>
              <AssetList
                assets={filteredAssets}
                selectedAssetId={selectedAssetId}
                onSelectAsset={setSelectedAssetId}
                searchQuery={assetSearchQuery}
                onSearchChange={setAssetSearchQuery}
                onAddAsset={handleAddAsset}
              />

              {selectedAsset ? (
                <AssetDetail
                  asset={selectedAsset}
                  onEdit={() => toast.info("Edit asset dialog coming soon")}
                  onRemoteConnect={() =>
                    toast.info("Remote connect coming soon")
                  }
                />
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  {filteredAssets.length === 0
                    ? "No assets match this filter."
                    : "Select an asset to view details"}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
